import { useState, useEffect, useRef } from 'react'
import { postAPI, imageLibraryAPI, calendarAPI } from '../../../services/api'

export default function PostDetailModal({
  selectedPost,
  onClose,
  onUpdatePost,
  statusBadge,
  getNewsDate,
  getNewsTime,
  accountId: propAccountId,
  inline = false,
  onPublishStateChange,
}) {
  const [editMode, setEditMode] = useState(true)
  const [editForm, setEditForm] = useState({
    title: selectedPost?.title || '',
    content: selectedPost?.content || selectedPost?.description || '',
    imageUrl: selectedPost?.imageUrl || ''
  })
  const [editSaving, setEditSaving] = useState(false)
  const [imageRegenerating, setImageRegenerating] = useState(false)
  const [captionRegenerating, setCaptionRegenerating] = useState(false)
  const [mediaList, setMediaList] = useState(selectedPost?.media || [])
  const [mediaLoading, setMediaLoading] = useState(false)
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0)
  const fileInputRef = useRef(null)
  const replaceTargetRef = useRef(null)

  // 인스타그램 업로드 상태
  const [publishing, setPublishing] = useState(false)

  // 이미지 라이브러리 관련 상태
  const [showLibrary, setShowLibrary] = useState(false)
  const [libraryImages, setLibraryImages] = useState([])
  const [libraryLoading, setLibraryLoading] = useState(false)

  // inline 모드에서 selectedPost가 바뀌면 상태 리셋
  useEffect(() => {
    if (selectedPost) {
      setEditMode(true)
      setEditForm({
        title: selectedPost.title || '',
        content: selectedPost.content || selectedPost.description || '',
        imageUrl: selectedPost.imageUrl || ''
      })
      setMediaList(selectedPost.media || [])
      setCurrentMediaIndex(0)
      setImageRegenerating(false)
      setCaptionRegenerating(false)
      setShowLibrary(false)
    }
  }, [selectedPost?.id])

  if (!selectedPost) return null

  // 대표 이미지: media[0] 우선, 없으면 imageUrl fallback
  const primaryImageUrl = mediaList.length > 0 ? mediaList[0].mediaUrl : (editMode ? editForm.imageUrl : selectedPost.imageUrl)

  const handleDeletePost = async () => {
    if (!window.confirm('이 게시물을 삭제하시겠습니까?\n삭제 후 복구할 수 없습니다.')) return
    try {
      await postAPI.delete(selectedPost.id)
      onUpdatePost(selectedPost.id, null)
      onClose()
    } catch (err) {
      alert('삭제 실패: ' + (err.message || '알 수 없는 오류'))
    }
  }

  const handleSavePost = async () => {
    try {
      setEditSaving(true)
      const res = await postAPI.update(selectedPost.id, {
        title: editForm.title,
        content: editForm.content,
        imageUrl: editForm.imageUrl
      })
      if (res.success) {
        onUpdatePost(selectedPost.id, res.data)
        setEditMode(false)
      }
    } catch (err) {
      alert('저장 실패: ' + err.message)
    } finally {
      setEditSaving(false)
    }
  }

  // === 기존 단일 이미지 변경 (media 없을 때 fallback) ===
  const handleLocalImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setImageRegenerating(true)
      const res = await postAPI.uploadImage(selectedPost.id, file)
      if (res.success) {
        onUpdatePost(selectedPost.id, res.data)
        setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl }))
      }
    } catch (err) {
      alert('이미지 업로드 실패: ' + err.message)
    } finally {
      setImageRegenerating(false)
    }
  }

  const handleRegenerateImage = async () => {
    const keyword = selectedPost.keyword?.keyword || selectedPost.title || ''
    try {
      setImageRegenerating(true)
      const res = await postAPI.regenerateImage(selectedPost.id, {
        keyword,
        source: 'GEMINI',
        accountId: selectedPost.keyword?.account?.id
      })
      if (res.success) {
        onUpdatePost(selectedPost.id, res.data)
        setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl }))
      }
    } catch (err) {
      alert('이미지 재생성 실패: ' + err.message)
    } finally {
      setImageRegenerating(false)
    }
  }

  const handleRegenerateCaption = async () => {
    const keyword = selectedPost.keyword?.keyword || selectedPost.title || ''
    try {
      setCaptionRegenerating(true)
      const res = await postAPI.regenerateCaption(selectedPost.id, { keyword })
      if (res.success) {
        onUpdatePost(selectedPost.id, res.data)
        setEditForm(prev => ({ ...prev, content: res.data.description || '' }))
      }
    } catch (err) {
      alert('캡션 재생성 실패: ' + err.message)
    } finally {
      setCaptionRegenerating(false)
    }
  }

  // === 미디어 관리 ===
  const handleUploadMedia = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setMediaLoading(true)
      if (replaceTargetRef.current) {
        const res = await postAPI.replaceMedia(selectedPost.id, replaceTargetRef.current, file)
        if (res.success) {
          setMediaList(res.data.media || [])
          onUpdatePost(selectedPost.id, res.data)
          setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl }))
        }
        replaceTargetRef.current = null
      } else {
        const res = await postAPI.uploadMedia(selectedPost.id, file)
        if (res.success) {
          setMediaList(res.data.media || [])
          onUpdatePost(selectedPost.id, res.data)
          setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl }))
        }
      }
    } catch (err) {
      alert(err.message || '미디어 업로드 실패')
    } finally {
      setMediaLoading(false)
    }
  }

  const handleGenerateMedia = async () => {
    const keyword = selectedPost.keyword?.keyword || selectedPost.title || ''
    try {
      setMediaLoading(true)
      const res = await postAPI.generateMedia(selectedPost.id, {
        keyword,
        source: 'GEMINI',
        accountId: selectedPost.keyword?.account?.id
      })
      if (res.success) {
        setMediaList(res.data.media || [])
        onUpdatePost(selectedPost.id, res.data)
        setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl }))
      }
    } catch (err) {
      alert(err.message || '이미지 생성 실패')
    } finally {
      setMediaLoading(false)
    }
  }

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('이 미디어를 삭제하시겠습니까?')) return
    try {
      setMediaLoading(true)
      const res = await postAPI.deleteMedia(selectedPost.id, mediaId)
      if (res.success) {
        const newMedia = res.data.media || []
        setMediaList(newMedia)
        setCurrentMediaIndex(prev => Math.min(prev, Math.max(0, newMedia.length - 1)))
        onUpdatePost(selectedPost.id, res.data)
        setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl || '' }))
      }
    } catch (err) {
      alert(err.message || '미디어 삭제 실패')
    } finally {
      setMediaLoading(false)
    }
  }

  const handleMoveMedia = async (currentIndex, direction) => {
    const newIndex = currentIndex + direction
    if (newIndex < 0 || newIndex >= mediaList.length) return
    const newOrder = [...mediaList]
    const [moved] = newOrder.splice(currentIndex, 1)
    newOrder.splice(newIndex, 0, moved)
    const mediaIds = newOrder.map(m => m.id)
    try {
      setMediaLoading(true)
      const res = await postAPI.reorderMedia(selectedPost.id, mediaIds)
      if (res.success) {
        setMediaList(res.data.media || [])
        onUpdatePost(selectedPost.id, res.data)
        setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl }))
      }
    } catch (err) {
      alert(err.message || '순서 변경 실패')
    } finally {
      setMediaLoading(false)
    }
  }

  const handleReplaceClick = (mediaId) => {
    replaceTargetRef.current = mediaId
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const handleAddClick = () => {
    replaceTargetRef.current = null
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  // === 이미지 라이브러리 ===
  const handleOpenLibrary = async () => {
    if (showLibrary) {
      setShowLibrary(false)
      return
    }
    try {
      setLibraryLoading(true)
      setShowLibrary(true)
      const accountId = propAccountId || selectedPost.keyword?.account?.id
      if (!accountId) {
        alert('계정 정보를 찾을 수 없습니다.')
        setShowLibrary(false)
        return
      }
      const res = await imageLibraryAPI.getMyImages(accountId)
      if (res.success) {
        setLibraryImages(res.data || [])
      }
    } catch (err) {
      alert('이미지 라이브러리 로드 실패: ' + err.message)
      setShowLibrary(false)
    } finally {
      setLibraryLoading(false)
    }
  }

  const handleAddFromLibrary = async (imageUrl) => {
    try {
      setMediaLoading(true)
      const res = await postAPI.addMediaFromLibrary(selectedPost.id, imageUrl)
      if (res.success) {
        setMediaList(res.data.media || [])
        onUpdatePost(selectedPost.id, res.data)
        setEditForm(prev => ({ ...prev, imageUrl: res.data.imageUrl }))
        setShowLibrary(false)
      }
    } catch (err) {
      alert(err.message || '이미지 추가 실패')
    } finally {
      setMediaLoading(false)
    }
  }

  const badge = statusBadge(selectedPost.uploadStatus)

  const content = (
    <div className={inline ? 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-y-auto' : 'bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto'} onClick={e => e.stopPropagation()}>
        {/* hidden file input for media upload */}
        <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleUploadMedia} />

        {/* 모달 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-bold dark:text-white flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">{editMode ? 'edit' : 'article'}</span>
            {editMode ? '게시물 편집' : '게시물 상세'}
          </h3>
          <div className="flex items-center gap-1">
            {!editMode && selectedPost.uploadStatus !== 'UPLOADED' && (
              <>
                <button onClick={() => setEditMode(true)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors" title="편집">
                  <span className="material-symbols-outlined text-primary text-xl">edit</span>
                </button>
                <button onClick={handleDeletePost} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="삭제">
                  <span className="material-symbols-outlined text-red-500 text-xl">delete</span>
                </button>
              </>
            )}
            {editMode && (
              <button onClick={handleDeletePost} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="삭제">
                <span className="material-symbols-outlined text-red-500 text-xl">delete</span>
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
              <span className="material-symbols-outlined dark:text-white">close</span>
            </button>
          </div>
        </div>

        {/* 이미지 캐러셀 영역 */}
        <div className="relative w-full aspect-square bg-slate-100 dark:bg-slate-800 overflow-hidden">
          {mediaList.length > 1 ? (
            <>
              {/* 캐러셀 이미지 */}
              <div className="w-full h-full relative">
                <img
                  src={mediaList[currentMediaIndex]?.mediaUrl}
                  alt={`${selectedPost.title} - ${currentMediaIndex + 1}`}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              </div>

              {/* 좌/우 네비게이션 버튼 */}
              {currentMediaIndex > 0 && (
                <button
                  onClick={() => setCurrentMediaIndex(prev => prev - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-colors"
                >
                  <span className="material-symbols-outlined text-lg">chevron_left</span>
                </button>
              )}
              {currentMediaIndex < mediaList.length - 1 && (
                <button
                  onClick={() => setCurrentMediaIndex(prev => prev + 1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-colors"
                >
                  <span className="material-symbols-outlined text-lg">chevron_right</span>
                </button>
              )}

              {/* 인디케이터 도트 */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {mediaList.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentMediaIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentMediaIndex
                        ? 'bg-white w-4'
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>

              {/* 이미지 카운터 */}
              <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 text-white text-xs font-medium rounded-full backdrop-blur-sm">
                {currentMediaIndex + 1} / {mediaList.length}
              </div>
            </>
          ) : (
            <>
              {/* 단일 이미지 또는 이미지 없음 */}
              {primaryImageUrl ? (
                <img src={primaryImageUrl} alt={selectedPost.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none' }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600">image</span>
                </div>
              )}
            </>
          )}

          {/* 편집 모드 & media 없을 때만 기존 단일 이미지 변경 UI 표시 */}
          {editMode && mediaList.length === 0 && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
              <p className="text-white text-xs font-semibold mb-2">이미지 변경</p>
              <div className="flex flex-wrap gap-2">
                <label className="px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">upload_file</span>
                  로컬 파일
                  <input type="file" accept="image/*" className="hidden" onChange={handleLocalImageUpload} />
                </label>
                <button
                  onClick={handleRegenerateImage}
                  disabled={imageRegenerating}
                  className="px-3 py-1.5 bg-primary/80 hover:bg-primary text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                >
                  <span className={`material-symbols-outlined text-sm ${imageRegenerating ? 'animate-spin' : ''}`}>
                    {imageRegenerating ? 'progress_activity' : 'autorenew'}
                  </span>
                  {imageRegenerating ? '생성 중...' : 'AI 재생성'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 미디어 관리 섹션 (편집 모드) */}
        {editMode && (
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                <span className="material-symbols-outlined text-base">image</span>
                미디어 ({mediaList.length}/10)
              </label>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleAddClick}
                  disabled={mediaLoading || mediaList.length >= 10}
                  className="px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-sm">upload_file</span>
                  파일
                </button>
                <button
                  onClick={handleOpenLibrary}
                  disabled={mediaLoading || mediaList.length >= 10}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 disabled:opacity-50 transition-colors ${
                    showLibrary
                      ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">photo_library</span>
                  라이브러리
                </button>
                <button
                  onClick={handleGenerateMedia}
                  disabled={mediaLoading || mediaList.length >= 10}
                  className="px-2.5 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                >
                  <span className={`material-symbols-outlined text-sm ${mediaLoading ? 'animate-spin' : ''}`}>
                    {mediaLoading ? 'progress_activity' : 'add_photo_alternate'}
                  </span>
                  AI 생성
                </button>
              </div>
            </div>

            {/* 이미지 라이브러리 패널 */}
            {showLibrary && (
              <div className="mb-3 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-violet-700 dark:text-violet-300 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">photo_library</span>
                    AI 생성 이미지 라이브러리
                  </p>
                  <button onClick={() => setShowLibrary(false)} className="p-0.5 hover:bg-violet-200 dark:hover:bg-violet-800 rounded">
                    <span className="material-symbols-outlined text-sm text-violet-500">close</span>
                  </button>
                </div>
                {libraryLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <span className="material-symbols-outlined text-violet-400 animate-spin">progress_activity</span>
                    <span className="text-xs text-violet-400 ml-2">로딩 중...</span>
                  </div>
                ) : libraryImages.length === 0 ? (
                  <p className="text-xs text-violet-400 text-center py-4">생성된 이미지가 없습니다.</p>
                ) : (
                  <div className="grid grid-cols-4 gap-1.5 max-h-48 overflow-y-auto">
                    {libraryImages.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => handleAddFromLibrary(img.imageUrl || img.thumbnailUrl)}
                        disabled={mediaLoading}
                        className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-violet-400 transition-colors group disabled:opacity-50"
                      >
                        <img
                          src={img.thumbnailUrl || img.imageUrl}
                          alt={img.keyword || ''}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = '' }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <span className="material-symbols-outlined text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity">add_circle</span>
                        </div>
                        {img.keyword && (
                          <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1 py-0.5">
                            <p className="text-[9px] text-white truncate">{img.keyword}</p>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {mediaList.length > 0 ? (
              <div className="grid grid-cols-5 gap-2">
                {mediaList.map((m, idx) => (
                  <div key={m.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700 hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => handleReplaceClick(m.id)}>
                      <img src={m.mediaUrl} alt="" className="size-full object-cover" />
                    </div>
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-primary text-white text-[9px] font-bold rounded">
                        대표
                      </span>
                    )}
                    {/* 순서 변경 */}
                    <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                      {idx > 0 && (
                        <button onClick={() => handleMoveMedia(idx, -1)}
                          className="p-0.5 bg-black/60 text-white rounded text-[10px]">
                          <span className="material-symbols-outlined text-xs">chevron_left</span>
                        </button>
                      )}
                      <span />
                      {idx < mediaList.length - 1 && (
                        <button onClick={() => handleMoveMedia(idx, 1)}
                          className="p-0.5 bg-black/60 text-white rounded text-[10px]">
                          <span className="material-symbols-outlined text-xs">chevron_right</span>
                        </button>
                      )}
                    </div>
                    {/* 삭제 */}
                    <button
                      onClick={() => handleDeleteMedia(m.id)}
                      className="absolute top-1 right-1 p-0.5 bg-red-500/80 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <span className="material-symbols-outlined text-xs">close</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-3">
                미디어가 없습니다. 파일 추가 또는 AI 생성으로 이미지를 추가하세요.
              </p>
            )}
          </div>
        )}

        {/* 콘텐츠 */}
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${badge.bg} ${badge.text} border ${badge.border}`}>
              {badge.label}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {getNewsDate(selectedPost)} {getNewsTime(selectedPost)}
            </span>
          </div>

          {editMode ? (
            <input
              type="text" value={editForm.title}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="제목을 입력하세요"
              className="w-full px-3 py-2 text-lg font-bold border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          ) : (
            <h4 className="font-bold text-lg dark:text-white leading-tight">
              {selectedPost.title || '제목 없음'}
            </h4>
          )}

          {editMode ? (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-slate-600 dark:text-slate-300">캡션</label>
                <button
                  onClick={handleRegenerateCaption}
                  disabled={captionRegenerating}
                  className="text-xs text-primary hover:underline flex items-center gap-0.5 disabled:opacity-50"
                >
                  <span className={`material-symbols-outlined text-xs ${captionRegenerating ? 'animate-spin' : ''}`}>
                    {captionRegenerating ? 'progress_activity' : 'auto_awesome'}
                  </span>
                  {captionRegenerating ? 'AI 생성 중...' : 'AI 캡션 재생성'}
                </button>
              </div>
              <textarea
                value={editForm.content}
                onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="캡션을 입력하세요" rows={6}
                className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-slate-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none leading-relaxed"
              />
              <p className="text-[10px] text-slate-400 mt-1 text-right">{editForm.content.length}/500자</p>
            </div>
          ) : (
            (selectedPost.content || selectedPost.description) && (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                <p className="text-sm dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                  {selectedPost.content || selectedPost.description}
                </p>
              </div>
            )
          )}

          {/* 보기 모드에서 media 있으면 썸네일 표시 (클릭 시 캐러셀 이동) */}
          {!editMode && mediaList.length > 1 && (
            <div>
              <p className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">
                미디어 ({mediaList.length}장)
              </p>
              <div className="grid grid-cols-5 gap-1.5">
                {mediaList.map((m, idx) => (
                  <div
                    key={m.id}
                    onClick={() => setCurrentMediaIndex(idx)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                      idx === currentMediaIndex
                        ? 'border-primary ring-1 ring-primary/30'
                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'
                    }`}
                  >
                    <img src={m.mediaUrl} alt="" className="size-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 메타 정보 */}
          <div className="grid grid-cols-2 gap-3">
            {selectedPost.keyword && (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5">
                <p className="text-[10px] text-slate-400 mb-0.5">키워드</p>
                <p className="text-sm font-semibold dark:text-white">{selectedPost.keyword.keyword || selectedPost.keyword}</p>
              </div>
            )}
            {selectedPost.sourceType && (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5">
                <p className="text-[10px] text-slate-400 mb-0.5">소스</p>
                <p className="text-sm font-semibold dark:text-white">
                  {selectedPost.sourceType === 'GENERATED' ? 'AI 생성' : selectedPost.sourceType === 'CALENDAR' ? '캘린더' : '크롤링'}
                </p>
              </div>
            )}
            {selectedPost.uploadedAt && (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5">
                <p className="text-[10px] text-slate-400 mb-0.5">업로드 시간</p>
                <p className="text-sm font-semibold dark:text-white">{selectedPost.uploadedAt?.replace('T', ' ').substring(0, 16)}</p>
              </div>
            )}
            {selectedPost.createdAt && (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5">
                <p className="text-[10px] text-slate-400 mb-0.5">생성일</p>
                <p className="text-sm font-semibold dark:text-white">{selectedPost.createdAt?.replace('T', ' ').substring(0, 16)}</p>
              </div>
            )}
            {selectedPost.scheduledAt && selectedPost.uploadStatus !== 'UPLOADED' && (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5">
                <p className="text-[10px] text-slate-400 mb-0.5">예정일</p>
                <p className="text-sm font-semibold dark:text-white">{selectedPost.scheduledAt?.replace('T', ' ').substring(0, 10)}</p>
              </div>
            )}
          </div>

          {editMode && (
            <div className="flex flex-col gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSavePost}
                  disabled={editSaving}
                  className="flex-1 py-2.5 bg-primary text-white font-semibold text-sm rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  <span className={`material-symbols-outlined text-sm ${editSaving ? 'animate-spin' : ''}`}>
                    {editSaving ? 'progress_activity' : 'save'}
                  </span>
                  {editSaving ? '저장 중...' : '저장'}
                </button>
                <button
                  onClick={() => {
                    setEditMode(false)
                    setEditForm({
                      title: selectedPost.title || '',
                      content: selectedPost.content || selectedPost.description || '',
                      imageUrl: selectedPost.imageUrl || ''
                    })
                    setMediaList(selectedPost.media || [])
                    setShowLibrary(false)
                  }}
                  className="flex-1 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold text-sm rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  취소
                </button>
              </div>

              {/* 인스타그램 수동 업로드 버튼 */}
              {selectedPost.uploadStatus !== 'UPLOADED' && (mediaList.length > 0 || selectedPost.imageUrl) && (
                <button
                  onClick={async () => {
                    if (!window.confirm('이 게시물을 인스타그램에 업로드하시겠습니까?')) return
                    setPublishing(true)
                    onPublishStateChange?.(true)
                    try {
                      const res = await calendarAPI.publishToInstagram(selectedPost.id)
                      if (res.success) {
                        alert('인스타그램에 업로드되었습니다!')
                        if (onUpdatePost) onUpdatePost()
                      } else {
                        alert('업로드 실패: ' + (res.message || '알 수 없는 오류'))
                      }
                    } catch (err) {
                      alert('업로드 실패: ' + (err.message || '알 수 없는 오류'))
                    } finally {
                      setPublishing(false)
                      onPublishStateChange?.(false)
                    }
                  }}
                  disabled={publishing}
                  className="w-full py-2.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white font-semibold text-sm rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  <span className={`material-symbols-outlined text-sm ${publishing ? 'animate-spin' : ''}`}>
                    {publishing ? 'progress_activity' : 'send'}
                  </span>
                  {publishing ? '업로드 중...' : '인스타그램에 업로드'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
  )

  if (inline) return content

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      {content}
    </div>
  )
}
